// components/CashFlowStatement.jsx
import React, { useState, useEffect } from 'react';
import generalLedgerAPI from '../api/generalLedgerAPI';
import { useAuth } from '../context/AuthContext';
import ReportToolbar from '../shared/ReportToolbar';
import { computePeriodRange } from '../shared/PeriodSelect';

const CashFlowStatement = () => {
  const { selectedClient } = useAuth();
  const accountingClientId = selectedClient?._id || localStorage.getItem('accountingClientId') || null;
  const accountingClientName = selectedClient?.name || localStorage.getItem('accountingClientName') || '';
  const [cashFlowStatement, setCashFlowStatement] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [period, setPeriod] = useState(() => ({ preset: 'THIS_MONTH', ...computePeriodRange('THIS_MONTH') }));
  const startDate = period.startDate;
  const endDate = period.endDate;
  const [fiscalYear, setFiscalYear] = useState(null);

  useEffect(() => {
    if (accountingClientId) {
      const fy = localStorage.getItem(`activeFiscalYear:${accountingClientId}`);
      if (fy) setFiscalYear(fy);
    }
  }, [accountingClientId]);

  useEffect(() => {
    const onFYChange = () => {
      if (accountingClientId) {
        const fy = localStorage.getItem(`activeFiscalYear:${accountingClientId}`);
        if (fy) setFiscalYear(fy);
      }
    };
    window.addEventListener('fiscalYear:changed', onFYChange);
    return () => window.removeEventListener('fiscalYear:changed', onFYChange);
  }, [accountingClientId]);

  const fetchCashFlowStatement = async () => {
    if (!startDate) { setError('Start date is required'); return; }
    setLoading(true);
    setError('');
    try {
      const response = await generalLedgerAPI.getCashFlowStatement(
        startDate, endDate, accountingClientId, fiscalYear
      );
      if (response.success) {
        setCashFlowStatement(response.data);
      } else {
        setError(response.message || 'Failed to fetch cash flow statement');
      }
    } catch (err) {
      setError(err.message || 'Error fetching cash flow statement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (startDate && accountingClientId) fetchCashFlowStatement();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, accountingClientId, fiscalYear]);

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-PH', { style: 'currency', currency: 'PHP' }).format(amount || 0);

  const formatCurrencyCSV = (amount) => parseFloat((amount || 0).toFixed(2));

  const cfs = cashFlowStatement;

  // ── CSV Export ──────────────────────────────────────────────────────────────
  const exportToCSV = () => {
    if (!cfs) return;

    const rows = [
      ['Section', 'Description', 'Amount'],
      // Operating
      ...(cfs.operatingActivities?.items || []).map((i) => [
        'Operating Activities', `"${i.description}"`, i.type !== 'header' ? formatCurrencyCSV(i.amount) : '',
      ]),
      ['Operating Activities', 'Net Cash from Operating Activities', formatCurrencyCSV(cfs.operatingActivities?.total)],
      [],
      // Investing
      ...(cfs.investingActivities?.items || []).map((i) => [
        'Investing Activities', `"${i.description}"`, formatCurrencyCSV(i.cashFlow),
      ]),
      ['Investing Activities', 'Net Cash from Investing Activities', formatCurrencyCSV(cfs.investingActivities?.total)],
      [],
      // Financing
      ...(cfs.financingActivities?.items || []).map((i) => [
        'Financing Activities', `"${i.description}"`, formatCurrencyCSV(i.cashFlow),
      ]),
      ['Financing Activities', 'Net Cash from Financing Activities', formatCurrencyCSV(cfs.financingActivities?.total)],
      [],
      ['Summary', 'Net Increase (Decrease) in Cash', formatCurrencyCSV(cfs.summary?.netIncreaseDecrease)],
      ['Summary', 'Cash at Beginning of Period', formatCurrencyCSV(cfs.cashBalances?.beginning)],
      ['Summary', 'Cash at End of Period', formatCurrencyCSV(cfs.cashBalances?.ending)],
    ];

    const csv = rows.map((r) => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `cash-flow-statement-${startDate}-to-${endDate}.csv`;
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // ── Print ───────────────────────────────────────────────────────────────────
  const handlePrint = () => {
    if (!cfs) return;

    const fmt = (amount) =>
      new Intl.NumberFormat('en-PH', { style: 'currency', currency: 'PHP' }).format(amount || 0);

    const secHeader = (label) =>
      `<tr style="background:#f9fafb;"><td colspan="2" style="padding:8px 8px 4px;font-weight:bold;border-top:2px solid #e5e7eb;font-size:11px;text-transform:uppercase;letter-spacing:.05em;color:#6b7280;">${label}</td></tr>`;

    const operatingRows = (cfs.operatingActivities?.items || []).map((i) => {
      if (i.type === 'header') return `<tr style="background:#f9fafb;"><td style="padding:4px 8px;font-weight:600;">${i.description}</td><td></td></tr>`;
      const color = i.type === 'positive' ? '#059669' : i.type === 'negative' ? '#dc2626' : '#111';
      const amt = i.type === 'negative' && i.amount > 0 ? `(${fmt(Math.abs(i.amount))})` : fmt(Math.abs(i.amount));
      return `<tr><td style="padding:3px 8px 3px 24px;">${i.description}</td><td style="padding:3px 8px;text-align:right;font-family:monospace;color:${color};">${amt}</td></tr>`;
    }).join('');

    const activityRows = (items = []) => items.map((i) => {
      const color = i.cashFlow >= 0 ? '#059669' : '#dc2626';
      const amt = i.cashFlow < 0 ? `(${fmt(Math.abs(i.cashFlow))})` : fmt(i.cashFlow);
      return `<tr><td style="padding:3px 8px 3px 24px;">${i.description}</td><td style="padding:3px 8px;text-align:right;font-family:monospace;color:${color};">${amt}</td></tr>`;
    }).join('');

    const totRow = (label, amount, dbl = false) => {
      const b = dbl ? 'border-top:3px double #111;border-bottom:3px double #111;' : 'border-top:1px solid #9ca3af;';
      return `<tr><td style="padding:5px 8px;font-weight:bold;${b}">${label}</td><td style="padding:5px 8px;text-align:right;font-family:monospace;font-weight:bold;${b}">${fmt(amount)}</td></tr>`;
    };

    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"/>
<title>Cash Flow Statement</title>
<style>
body{font-family:'Courier New',monospace;font-size:12px;color:#111;margin:40px}
.hdr{text-align:center;margin-bottom:28px}.hdr h1{font-size:18px;margin:0 0 2px}.hdr h2{font-size:13px;margin:0;font-weight:normal}
.sub{font-size:11px;color:#6b7280;margin-top:6px}
table{width:100%;border-collapse:collapse;margin-bottom:4px}
.tb{text-align:center;margin-bottom:20px}.tb button{padding:7px 18px;font-size:12px;cursor:pointer;background:#0d9488;color:#fff;border:none;border-radius:4px;font-family:'Courier New',monospace}
@media print{.tb{display:none}@page{margin:20mm}}
</style></head><body>
<div class="tb"><button onclick="window.print()">🖨 Print / Save as PDF</button></div>
<div class="hdr">
${accountingClientName ? `<div style="font-size:16px;font-weight:bold;margin-bottom:2px;">${accountingClientName}</div>` : ''}
<h1>Statement of Cash Flows</h1>
<div class="sub">${cfs.period?.displayPeriod || `${startDate} to ${endDate}`}${fiscalYear ? ` &nbsp;|&nbsp; Fiscal Year: ${fiscalYear}` : ''}</div>
</div>
<table><tbody>
${secHeader('Operating Activities')}
${operatingRows}
${totRow('Net Cash from Operating Activities', cfs.operatingActivities?.total)}

${secHeader('Investing Activities')}
${cfs.investingActivities?.items?.length ? activityRows(cfs.investingActivities.items) : '<tr><td style="padding:3px 8px 3px 24px;color:#9ca3af;">No investing activities</td><td></td></tr>'}
${totRow('Net Cash from Investing Activities', cfs.investingActivities?.total)}

${secHeader('Financing Activities')}
${cfs.financingActivities?.items?.length ? activityRows(cfs.financingActivities.items) : '<tr><td style="padding:3px 8px 3px 24px;color:#9ca3af;">No financing activities</td><td></td></tr>'}
${totRow('Net Cash from Financing Activities', cfs.financingActivities?.total)}

${totRow('Net Increase (Decrease) in Cash', cfs.summary?.netIncreaseDecrease, true)}
<tr><td style="padding:3px 8px;">Cash at Beginning of Period</td><td style="padding:3px 8px;text-align:right;font-family:monospace;">${fmt(cfs.cashBalances?.beginning)}</td></tr>
${totRow('Cash at End of Period', cfs.cashBalances?.ending, true)}
</tbody></table></body></html>`;

    const win = window.open('', '_blank', 'width=800,height=1000');
    win.document.write(html);
    win.document.close();
    setTimeout(() => { win.focus(); win.print(); }, 500);
  };

  return (
    <div className="w-full min-h-screen bg-gray-50 p-8">
      <div className="max-w-3xl mx-auto">

        {/* Controls */}
        <div className="mb-8 text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-6">CASH FLOW STATEMENT</h1>

          <div className="flex justify-center mb-4">
            <ReportToolbar
              period={period}
              onPeriodChange={setPeriod}
              fiscalYear={fiscalYear}
              onExportCSV={cfs ? exportToCSV : undefined}
              onPrint={cfs ? handlePrint : undefined}
              onRefresh={fetchCashFlowStatement}
              refreshing={loading}
            />
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-6 rounded-md bg-red-50 p-4">
            <div className="text-sm text-red-700">{error}</div>
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
          </div>
        )}

        {/* Report body */}
        {!loading && cfs && (
          <div className="bg-white p-10 font-mono text-sm max-w-3xl mx-auto">

            {/* Report header */}
            <div className="text-center mb-8">
              {accountingClientName && (
                <div className="text-xl font-bold text-gray-900 mb-1">{accountingClientName}</div>
              )}
              <div className="text-2xl font-bold text-gray-900 tracking-wide">STATEMENT OF CASH FLOWS</div>
              <div className="mt-2 text-xs text-gray-500">
                {cfs.period?.displayPeriod || `${startDate} to ${endDate}`}
                {fiscalYear && <>&nbsp;&nbsp;|&nbsp;&nbsp;Fiscal Year: {fiscalYear}</>}
              </div>
            </div>

            {/* ── Operating Activities ──────────────────────────────────── */}
            <div className="mt-4 mb-1 text-xs font-bold text-gray-500 uppercase tracking-widest">Operating Activities</div>
            {(cfs.operatingActivities?.items || []).map((item, idx) => {
              if (item.type === 'header') return (
                <div key={idx} className="grid grid-cols-12 py-1 mt-2">
                  <div className="col-span-9 font-semibold text-gray-800">{item.description}</div>
                  <div className="col-span-3"></div>
                </div>
              );
              const color = item.type === 'positive' ? 'text-teal-700' : item.type === 'negative' ? 'text-red-600' : 'text-gray-900';
              const display = item.type === 'negative' && item.amount > 0
                ? `(${formatCurrency(Math.abs(item.amount))})`
                : formatCurrency(Math.abs(item.amount));
              return (
                <div key={idx} className="grid grid-cols-12 py-1 border-b border-gray-100">
                  <div className="col-span-9 text-gray-700 pl-4">{item.description}</div>
                  <div className={`col-span-3 text-right font-mono ${color}`}>{display}</div>
                </div>
              );
            })}
            <div className="grid grid-cols-12 py-2 border-t border-gray-400 mt-1 font-bold">
              <div className="col-span-9 text-gray-800">Net Cash from Operating Activities</div>
              <div className={`col-span-3 text-right font-mono ${(cfs.operatingActivities?.total || 0) >= 0 ? 'text-teal-800' : 'text-red-700'}`}>
                {formatCurrency(cfs.operatingActivities?.total)}
              </div>
            </div>

            {/* ── Investing Activities ──────────────────────────────────── */}
            <div className="mt-6 mb-1 text-xs font-bold text-gray-500 uppercase tracking-widest">Investing Activities</div>
            {cfs.investingActivities?.items?.length ? (
              cfs.investingActivities.items.map((item, idx) => (
                <div key={idx} className="grid grid-cols-12 py-1 border-b border-gray-100">
                  <div className="col-span-9 text-gray-700 pl-4">{item.description}</div>
                  <div className={`col-span-3 text-right font-mono ${item.cashFlow >= 0 ? 'text-teal-700' : 'text-red-600'}`}>
                    {item.cashFlow < 0 ? `(${formatCurrency(Math.abs(item.cashFlow))})` : formatCurrency(item.cashFlow)}
                  </div>
                </div>
              ))
            ) : (
              <div className="py-2 pl-4 text-gray-400 text-xs">No investing activities in this period</div>
            )}
            <div className="grid grid-cols-12 py-2 border-t border-gray-400 mt-1 font-bold">
              <div className="col-span-9 text-gray-800">Net Cash from Investing Activities</div>
              <div className={`col-span-3 text-right font-mono ${(cfs.investingActivities?.total || 0) >= 0 ? 'text-teal-800' : 'text-red-700'}`}>
                {formatCurrency(cfs.investingActivities?.total)}
              </div>
            </div>

            {/* ── Financing Activities ─────────────────────────────────── */}
            <div className="mt-6 mb-1 text-xs font-bold text-gray-500 uppercase tracking-widest">Financing Activities</div>
            {cfs.financingActivities?.items?.length ? (
              cfs.financingActivities.items.map((item, idx) => (
                <div key={idx} className="grid grid-cols-12 py-1 border-b border-gray-100">
                  <div className="col-span-9 text-gray-700 pl-4">{item.description}</div>
                  <div className={`col-span-3 text-right font-mono ${item.cashFlow >= 0 ? 'text-teal-700' : 'text-red-600'}`}>
                    {item.cashFlow < 0 ? `(${formatCurrency(Math.abs(item.cashFlow))})` : formatCurrency(item.cashFlow)}
                  </div>
                </div>
              ))
            ) : (
              <div className="py-2 pl-4 text-gray-400 text-xs">No financing activities in this period</div>
            )}
            <div className="grid grid-cols-12 py-2 border-t border-gray-400 mt-1 font-bold">
              <div className="col-span-9 text-gray-800">Net Cash from Financing Activities</div>
              <div className={`col-span-3 text-right font-mono ${(cfs.financingActivities?.total || 0) >= 0 ? 'text-teal-800' : 'text-red-700'}`}>
                {formatCurrency(cfs.financingActivities?.total)}
              </div>
            </div>

            {/* ── Summary ──────────────────────────────────────────────── */}
            <div className="grid grid-cols-12 py-3 mt-4 border-t-2 border-b-2 border-double border-gray-700 font-bold text-base">
              <div className="col-span-9 text-gray-900">Net Increase (Decrease) in Cash</div>
              <div className={`col-span-3 text-right font-mono ${(cfs.summary?.netIncreaseDecrease || 0) >= 0 ? 'text-teal-800' : 'text-red-700'}`}>
                {formatCurrency(cfs.summary?.netIncreaseDecrease)}
              </div>
            </div>
            <div className="grid grid-cols-12 py-1 mt-2">
              <div className="col-span-9 text-gray-700">Cash at Beginning of Period</div>
              <div className="col-span-3 text-right font-mono text-gray-900">{formatCurrency(cfs.cashBalances?.beginning)}</div>
            </div>
            <div className="grid grid-cols-12 py-3 border-t-2 border-b-2 border-double border-gray-700 font-bold text-base mt-1">
              <div className="col-span-9 text-gray-900">Cash at End of Period</div>
              <div className="col-span-3 text-right font-mono text-teal-800">{formatCurrency(cfs.cashBalances?.ending)}</div>
            </div>

          </div>
        )}

      </div>
    </div>
  );
};

export default CashFlowStatement;
